import { parseTimestampToNumber, toIsoStringWithTimezone } from './time'

export const DISPLAY_MODE_ELAPSED = 'elapsed'
export const DISPLAY_MODE_REMAINING = 'remaining'

export type FastDisplayMode = typeof DISPLAY_MODE_ELAPSED | typeof DISPLAY_MODE_REMAINING

export type FastEntryRange = {
   start: number
   end: number
}

export type FastEntry = {
   startTime: number
   endTime?: number | null
   duration: number
   intendedDuration?: number | null
   actualDuration?: number | null
   durationUpdatedAt: number
   notifiedCompletion: boolean
   displayMode: FastDisplayMode
   notes?: string | null
   clientEntryId?: string | null
   historyId?: number | null
   fatBurned?: number | null
   isCurrent?: boolean | null
}

export type HistoryTimeBlockRangeCollection = {
   ranges: FastEntryRange[]
   currentRange: FastEntryRange | null
}

/**
 * Returns the value when it is a finite number, otherwise null.
 *
 * @param value - The candidate value.
 * @returns The finite number, or null.
 */
function finiteNumber(value: unknown): number | null {
   return typeof value === 'number' && Number.isFinite(value) ? value : null
}

/**
 * Returns the value when it is a string, otherwise null.
 *
 * @param value - The candidate value.
 * @returns The string, or null.
 */
function stringOrNull(value: unknown): string | null {
   return typeof value === 'string' ? value : null
}

/**
 * Validates a timer display-mode preference (`elapsed`/`remaining`).
 *
 * @param value - The candidate mode.
 * @returns The normalized mode, or null when unrecognized.
 */
export function normalizeDisplayModePreference(value: unknown): FastDisplayMode | null {
   if (value === DISPLAY_MODE_REMAINING) {
      return DISPLAY_MODE_REMAINING
   }
   if (value === DISPLAY_MODE_ELAPSED) {
      return DISPLAY_MODE_ELAPSED
   }
   return null
}

/**
 * Deserializes a stored fast entry, deriving its duration and normalizing fields.
 *
 * @param entry - The raw stored entry.
 * @returns The fast entry, or null when the start time or duration is invalid.
 */
export function deserializeFastEntry(entry: unknown): FastEntry | null {
   if (!entry || typeof entry !== 'object') {
      return null
   }

   const record = entry as Record<string, unknown>
   const startTime = parseTimestampToNumber(record.startTime)
   if (startTime === null) {
      return null
   }

   const endTime = parseTimestampToNumber(record.endTime)
   const intendedDuration = finiteNumber(record.intendedDuration)
   const storedDuration = finiteNumber(record.duration) ?? intendedDuration
   const duration =
      storedDuration ??
      (endTime !== null && endTime > startTime ? endTime - startTime : null)
   if (duration === null || duration <= 0) {
      return null
   }

   return {
      startTime,
      endTime,
      duration,
      intendedDuration: intendedDuration ?? duration,
      actualDuration: finiteNumber(record.actualDuration),
      durationUpdatedAt: parseTimestampToNumber(record.durationUpdatedAt) ?? startTime,
      notifiedCompletion: record.notifiedCompletion === true,
      displayMode:
         normalizeDisplayModePreference(record.displayMode) ?? DISPLAY_MODE_ELAPSED,
      notes: stringOrNull(record.notes),
      clientEntryId: stringOrNull(record.clientEntryId),
      historyId: finiteNumber(record.historyId),
      fatBurned: finiteNumber(record.fatBurned),
      isCurrent: record.isCurrent === true,
   }
}

/**
 * Converts a fast entry to its persisted form with ISO start/end times.
 *
 * @param entry - The in-memory fast entry.
 * @returns The persistable record.
 */
export function prepareFastEntryForPersistence(entry: FastEntry) {
   const sanitized: Record<string, unknown> = { ...entry }
   if (Object.prototype.hasOwnProperty.call(sanitized, 'startTime')) {
      const isoStart = toIsoStringWithTimezone(sanitized.startTime)
      sanitized.startTime = isoStart ?? null
   }
   if (Object.prototype.hasOwnProperty.call(sanitized, 'endTime')) {
      const isoEnd = toIsoStringWithTimezone(sanitized.endTime)
      sanitized.endTime = isoEnd ?? null
   }

   return sanitized
}

/**
 * Ensures a fast entry has a valid `durationUpdatedAt`, filling it from a fallback when missing (mutates the entry).
 *
 * @param entry - The fast entry to patch in place.
 * @param fallbackTimestamp - Timestamp used when the field is missing/invalid (defaults to now).
 */
export function ensureCurrentFastDurationUpdatedAt(
   entry: FastEntry,
   fallbackTimestamp = Date.now(),
) {
   const normalizedFallback =
      Number.isFinite(fallbackTimestamp) ? fallbackTimestamp : Date.now()
   if (
      typeof entry.durationUpdatedAt !== 'number' ||
      !Number.isFinite(entry.durationUpdatedAt)
   ) {
      entry.durationUpdatedAt = normalizedFallback
   }
}

/**
 * Computes a fast's [start,end] epoch-ms range, optionally falling back to its duration for the end.
 *
 * @param entry - The fast entry.
 * @param options - Set `allowDurationFallback` to false to require an explicit end time.
 * @returns The range, or null when it cannot be determined.
 */
function getFastRange(
   entry: FastEntry | null | undefined,
   options: { allowDurationFallback?: boolean } = {},
): FastEntryRange | null {
   if (!entry || typeof entry !== 'object') {
      return null
   }
   const start = finiteNumber(entry.startTime)
   if (start === null) {
      return null
   }

   let end = finiteNumber(entry.endTime)
   if (end === null && options.allowDurationFallback !== false) {
      const fallbackDuration =
         finiteNumber(entry.duration) ?? finiteNumber(entry.intendedDuration)
      if (fallbackDuration !== null) {
         end = start + fallbackDuration
      }
   }

   if (end === null || end <= start) {
      return null
   }

   return { start, end }
}

/**
 * Returns a fast entry's duration in milliseconds, preferring the actual start/end span.
 *
 * @param entry - The fast entry, if any.
 * @returns The duration in milliseconds (0 when unavailable).
 */
export function getFastEntryDurationMs(entry: FastEntry | null | undefined) {
   if (!entry || typeof entry !== 'object') {
      return 0
   }
   const start = finiteNumber(entry.startTime)
   const end = finiteNumber(entry.endTime)
   if (start !== null && end !== null && end > start) {
      return end - start
   }
   return finiteNumber(entry.duration) ?? finiteNumber(entry.intendedDuration) ?? 0
}

/**
 * Removes duplicate fast entries that share the same start/end range.
 *
 * @param entries - The fast entries.
 * @returns The de-duplicated entries.
 */
export function dedupeFastHistoryEntries(entries: FastEntry[]) {
   const seen = new Set<string>()
   const result: FastEntry[] = []

   for (const entry of entries) {
      if (!entry || typeof entry !== 'object') {
         continue
      }

      const range = getFastRange(entry, { allowDurationFallback: true })
      if (!range) {
         continue
      }

      const key = `${range.start}-${range.end}`
      if (seen.has(key)) {
         continue
      }
      seen.add(key)
      result.push(entry)
   }

   return result
}

/**
 * Sorts fast entries in place by end time, newest first.
 *
 * @param entries - The fast entries to sort (mutated).
 */
export function sortFastEntriesDescending(entries: FastEntry[]) {
   entries.sort((a, b) => {
      const aRange = getFastRange(a)
      const bRange = getFastRange(b)
      const aEnd = aRange ? aRange.end : 0
      const bEnd = bRange ? bRange.end : 0
      return bEnd - aEnd
   })
}

/**
 * Collects the time-block ranges for a list of fasts and identifies the current one.
 *
 * @param entries - The fast entries (may contain nullish holes).
 * @returns The ranges and the current in-progress range, if any.
 */
export function collectTimeBlockRanges(
   entries: Array<FastEntry | null | undefined>,
): HistoryTimeBlockRangeCollection {
   if (!Array.isArray(entries)) {
      return {
         ranges: [],
         currentRange: null,
      }
   }

   const ranges: FastEntryRange[] = []
   let currentRange: FastEntryRange | null = null
   for (const entry of entries) {
      const range = getFastRange(entry, {
         allowDurationFallback: true,
      })
      if (!range) {
         continue
      }
      const normalizedRange = {
         start: range.start,
         end: range.end,
      }
      ranges.push(normalizedRange)
      if (
         currentRange === null &&
         entry &&
         typeof entry === 'object' &&
         entry.isCurrent === true
      ) {
         currentRange = normalizedRange
      }
   }
   return {
      ranges,
      currentRange,
   }
}
